import { Link } from 'wouter';
import { ArrowLeft, Shield, Database, Bell, MapPin, Camera, Share2, Lock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Privacy Policy Page
 *
 * Comprehensive privacy policy required for app store submission.
 * Covers all data collection, storage, and sharing practices.
 */

const LAST_UPDATED = 'January 24, 2026';
const CONTACT_EMAIL = 'privacy@sunsquatcher.com';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl py-6 sm:py-8 px-4">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="mb-3 sm:mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Privacy Policy</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-6 sm:space-y-8 text-sm sm:text-base">
          {/* Introduction */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Shield className="h-5 w-5 text-primary" />
              Introduction
            </h2>
            <p>
              SunSquatcher ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our mobile
              application and web service (collectively, the "Service").
            </p>
            <p>
              Please read this Privacy Policy carefully. By using the Service, you agree to the collection and
              use of information in accordance with this policy. If you do not agree with our policies and
              practices, do not use the Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Database className="h-5 w-5 text-primary" />
              Information We Collect
            </h2>

            <h3 className="font-medium mt-4">Personal Information</h3>
            <p>When you create an account, we may collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Email address (for account authentication and notifications)</li>
              <li>Display name (optional, for personalization)</li>
              <li>Profile picture (optional, stored securely)</li>
              <li>Password (encrypted, never stored in plain text)</li>
            </ul>

            <h3 className="font-medium mt-4">Location Data</h3>
            <p className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-1" />
              <span>
                We collect location data to provide sunrise/sunset predictions for your area. This includes:
              </span>
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>GPS coordinates when you use "Use My Location" feature</li>
              <li>Saved location preferences (cities, coordinates you save)</li>
              <li>Location history for providing relevant forecasts</li>
            </ul>
            <p>
              <strong>Note:</strong> You can use SunSquatcher without sharing your precise location by manually
              searching for locations.
            </p>

            <h3 className="font-medium mt-4">Photos</h3>
            <p className="flex items-start gap-2">
              <Camera className="h-5 w-5 text-primary shrink-0 mt-1" />
              <span>
                If you use our Photo Analyzer feature, we collect photos you upload to analyze sky conditions.
                These photos are:
              </span>
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Stored securely in encrypted cloud storage</li>
              <li>Analyzed using AI to determine sunrise/sunset quality</li>
              <li>Retained until you delete them from your account</li>
              <li>Never shared with third parties without your consent</li>
            </ul>

            <h3 className="font-medium mt-4">Usage Data</h3>
            <p>We automatically collect certain information when you use the Service:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Device information (device type, operating system, browser type)</li>
              <li>Usage patterns (features used, time spent, navigation paths)</li>
              <li>Crash reports and performance data</li>
              <li>IP address (for security and analytics)</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Share2 className="h-5 w-5 text-primary" />
              How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide accurate sunrise/sunset quality predictions</li>
              <li>Send push notifications about upcoming exceptional events (if enabled)</li>
              <li>Analyze photos to provide sky quality assessments</li>
              <li>Improve our prediction algorithms and user experience</li>
              <li>Communicate important updates about the Service</li>
              <li>Detect and prevent fraud, abuse, and security issues</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          {/* Push Notifications */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Bell className="h-5 w-5 text-primary" />
              Push Notifications
            </h2>
            <p>
              With your permission, we send push notifications to alert you about exceptional sunrise/sunset
              conditions in your saved locations. You can:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Enable or disable notifications at any time in Settings</li>
              <li>Customize notification preferences by quality threshold</li>
              <li>Choose which locations receive notifications</li>
              <li>Set quiet hours to prevent notifications at certain times</li>
            </ul>
            <p>
              We use Firebase Cloud Messaging (FCM) for Android and Apple Push Notification Service (APNs) for
              iOS to deliver notifications. Device tokens are stored securely and used solely for delivering
              your requested notifications.
            </p>
          </section>

          {/* Data Storage and Security */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Lock className="h-5 w-5 text-primary" />
              Data Storage and Security
            </h2>
            <p>
              Your data is stored securely using Amazon Web Services (AWS) infrastructure with the following
              protections:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption at rest using AES-256</li>
              <li>Encryption in transit using TLS 1.3</li>
              <li>Regular security audits and penetration testing</li>
              <li>Access controls and authentication requirements</li>
              <li>Data backup and disaster recovery procedures</li>
            </ul>
            <p>
              While we implement industry-standard security measures, no method of transmission or storage is
              100% secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Third-Party Services</h2>
            <p>We use the following third-party services that may collect information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Open-Meteo:</strong> Weather data API (receives location coordinates to provide forecasts)
              </li>
              <li>
                <strong>Amazon Web Services:</strong> Cloud infrastructure (data storage and processing)
              </li>
              <li>
                <strong>Sentry:</strong> Error tracking and performance monitoring (receives anonymized crash reports)
              </li>
              <li>
                <strong>MapLibre:</strong> Map display (receives location coordinates for map rendering)
              </li>
              <li>
                <strong>Amazon Cognito:</strong> Authentication service (manages user credentials)
              </li>
            </ul>
            <p>
              Each third-party service has its own privacy policy. We encourage you to review their policies.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Withdraw consent:</strong> Opt out of optional data processing</li>
              <li><strong>Object:</strong> Object to certain types of data processing</li>
            </ul>
            <p>
              To exercise these rights, please contact us at {CONTACT_EMAIL}.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Data Retention</h2>
            <p>We retain your information for as long as:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your account is active</li>
              <li>Needed to provide you with our services</li>
              <li>Required by law or for legitimate business purposes</li>
            </ul>
            <p>
              When you delete your account, we will delete or anonymize your personal data within 30 days,
              except where we are required to retain it for legal purposes.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Children's Privacy</h2>
            <p>
              SunSquatcher is not intended for users under 13 years of age. We do not knowingly collect
              personal information from children under 13. If we discover that we have collected personal
              information from a child under 13, we will delete it immediately.
            </p>
          </section>

          {/* Changes to This Policy */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by
              posting the new Privacy Policy on this page and updating the "Last updated" date. You are
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-semibold">
              <Mail className="h-5 w-5 text-primary" />
              Contact Us
            </h2>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>By email: {CONTACT_EMAIL}</li>
              <li>Through the in-app feedback feature</li>
            </ul>
          </section>

          {/* California Residents */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">California Residents</h2>
            <p>
              If you are a California resident, you have additional rights under the California Consumer
              Privacy Act (CCPA), including the right to know what personal information we collect and
              the right to opt out of the sale of your personal information. Note that we do not sell
              your personal information.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-lg sm:text-xl font-semibold">International Users</h2>
            <p>
              If you are accessing the Service from outside the United States, please be aware that your
              information may be transferred to, stored, and processed in the United States. By using the
              Service, you consent to this transfer.
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border">
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <Link href="/terms">
              <Button variant="outline">Terms of Service</Button>
            </Link>
            <Link href="/">
              <Button variant="ghost">Back to Home</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
